import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
  ReactNode,
} from "react";
import api from "../../api/api"; // Axios instance for API calls
import { PaginationInfo } from "../../types/api";

interface WorkCategoriesContextType {
  workCategories: any[];
  pagination: PaginationInfo | null;
  loading: boolean;
  fetchWorkCategories: (page?: number, perPage?: number) => Promise<void>;
  addWorkCategory: (category: any) => Promise<void>;
  editWorkCategory: (category: any) => Promise<void>;
  deleteWorkCategory: (id: number | string) => Promise<void>;
}

export const WorkCategoriesContext = createContext<
  WorkCategoriesContextType | undefined
>(undefined);

interface WorkCategoriesProviderProps {
  children: ReactNode;
}

export const useWorkCategoriesContext = (): WorkCategoriesContextType => {
  const context = useContext(WorkCategoriesContext);
  if (!context) {
    throw new Error(
      "useWorkCategoriesContext must be used within WorkCategoriesProvider",
    );
  }
  return context;
};

const WorkCategoriesProvider: React.FC<WorkCategoriesProviderProps> = ({
  children,
}) => {
  const [workCategories, setWorkCategories] = useState<any[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Fetch all work categories with pagination
  const fetchWorkCategories = useCallback(async (page: number = 1, perPage: number = 15) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      const response = await api.get(`/work-categories?${params.toString()}`);
      
      // Handle paginated response structure
      let categoriesData = [];
      let paginationData = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          categoriesData = responseData.data.data;
          paginationData = responseData.data.pagination || responseData.pagination;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          categoriesData = responseData.data;
          paginationData = responseData.pagination;
        }
      } else if (Array.isArray(responseData?.data)) {
        categoriesData = responseData.data;
      } else if (Array.isArray(responseData)) {
        categoriesData = responseData;
      }
      
      setWorkCategories(Array.isArray(categoriesData) ? categoriesData : []);
      setPagination(paginationData);
    } catch (error) {
      console.error("Failed to fetch work categories:", error);
      setWorkCategories([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Add a new work category
  const addWorkCategory = async (category: any): Promise<void> => {
    try {
      const response = await api.post("/work-categories", category);
      setWorkCategories((prev) => [...prev, response.data?.data || response.data]);
    } catch (error) {
      console.error("Failed to add work category:", error);
      throw error;
    }
  };

  // Edit an existing work category
  const editWorkCategory = async (category: any): Promise<void> => {
    try {
      const response = await api.put(`/work-categories/${category.id}`, category);
      setWorkCategories((prev) =>
        prev.map((item) => (item.id === category.id ? (response.data?.data || response.data) : item))
      );
    } catch (error) {
      console.error("Failed to edit work category:", error);
      throw error;
    }
  };

  // Delete a work category
  const deleteWorkCategory = async (id: number | string): Promise<void> => {
    try {
      await api.delete(`/work-categories/${id}`);
      setWorkCategories((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      console.error("Failed to delete work category:", error);
      throw error;
    }
  };

  useEffect(() => {
    fetchWorkCategories(1, 15);
  }, [fetchWorkCategories]);

  return (
    <WorkCategoriesContext.Provider
      value={{
        workCategories,
        pagination,
        loading,
        fetchWorkCategories,
        addWorkCategory,
        editWorkCategory,
        deleteWorkCategory,
      }}
    >
      {children}
    </WorkCategoriesContext.Provider>
  );
};

export default WorkCategoriesProvider;
